import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Icon, type IconName } from '../../components/Icon';
import { useWidthGlide } from '../../hooks/useWidthGlide';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { matches, type MenuItem } from './menu-items';
import { MenuRow, MenuSearch, MenuSection } from './MenuPanel';
import { finePointer, moveWithArrows, usePopover, useSearchFocus } from './usePopover';

interface OptionMenuProps<T extends string> {
  readonly label: string;
  readonly icon: IconName;
  /** The choices; the first is the default. */
  readonly items: readonly (MenuItem & { readonly id: T })[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  /**
   * For a long list, such as the languages: a search on top, and the rest of the choices under
   * this heading, apart from the default.
   */
  readonly heading?: string;
  /** Pins choices under the heading; the default always stays first. */
  readonly onPin?: ((value: T) => void) | undefined;
}

/**
 * Length and Language, in the editor's toolbar, like Tone: a chip naming the choice, or the
 * option until it's changed, which opens a menu of the choices, each with its icon and a line on
 * what it does, the one in force ticked. It hangs from the toolbar, under its chip, so the
 * scrolling row of chips can't clip it.
 */
export function OptionMenu<T extends string>({
  label,
  icon,
  items,
  value,
  onChange,
  heading,
  onPin,
}: OptionMenuProps<T>) {
  const { t } = useI18n();
  const { open, shown, present, leaving, setOpen, rootRef, triggerRef, onBlur } = usePopover();
  const searchRef = useSearchFocus(shown && heading !== undefined);
  const panelRef = useRef<HTMLDivElement>(null);
  const pinFocus = useRef<T | null>(null);
  const [query, setQuery] = useState('');
  const [start, setStart] = useState(0);
  const panelId = useId();
  const [fallback, ...rest] = items;
  const current = items.find((item) => item.id === value) ?? fallback;
  const isSet = current !== fallback;
  useWidthGlide(triggerRef, isSet ? current?.name : label);

  // Without a search to type in, the choice in force takes the focus, for the arrow keys.
  useEffect(() => {
    if (shown && heading === undefined && finePointer())
      panelRef.current?.querySelector<HTMLElement>('[aria-current="true"]')?.focus();
  }, [shown, heading]);

  // Pinning moves a row between sections, so restore focus to its new button.
  useLayoutEffect(() => {
    if (pinFocus.current === null) return;
    const button = [
      ...(panelRef.current?.querySelectorAll<HTMLButtonElement>('.menu-row-pin') ?? []),
    ].find((candidate) => candidate.dataset.item === pinFocus.current);
    pinFocus.current = null;
    button?.focus();
  });

  const choose = (item: MenuItem & { readonly id: T }) => () => {
    setOpen(false);
    onChange(item.id);
  };
  const row = (item: MenuItem & { readonly id: T }) => (
    <MenuRow
      key={item.id}
      item={item}
      current={item.id === current?.id}
      onSelect={choose(item)}
      onPin={
        onPin && item !== fallback
          ? () => {
              pinFocus.current = item.id;
              onPin(item.id);
            }
          : undefined
      }
    />
  );
  const found = items.filter((item) => matches(item, query));
  const pinned = rest.filter((item) => item.pinned && found.includes(item));
  const unpinned = rest.filter((item) => !item.pinned && found.includes(item));
  const first = fallback && found.includes(fallback) ? fallback : (pinned[0] ?? unpinned[0]);

  return (
    <div className="option-menu" ref={rootRef} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className={cx('option option-button', isSet && 'is-set')}
        aria-label={label}
        title={`${label}: ${current?.name ?? ''}`}
        aria-haspopup="dialog"
        aria-expanded={shown}
        aria-controls={panelId}
        onClick={(event) => {
          if (!open) {
            setQuery('');
            // Under its chip, measured from the toolbar's start, which the menu hangs from.
            const chip = event.currentTarget.getBoundingClientRect();
            const bar = event.currentTarget.closest('.editor-toolbar')?.getBoundingClientRect();
            const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
            if (bar) setStart(rtl ? bar.right - chip.right : chip.left - bar.left);
          }
          setOpen((now) => !now);
        }}
      >
        <span className="option-icon" aria-hidden="true">
          <Icon name={icon} />
        </span>
        {/* Keyed by what it says, so a new choice slides in rather than swapping in place. */}
        <span key={isSet ? value : ''} className="option-value" aria-hidden="true">
          {isSet ? current?.name : label}
        </span>
        <span className="option-chevron" aria-hidden="true">
          <Icon name="chevron" />
        </span>
      </button>
      {present && (
        <div
          ref={panelRef}
          className={cx('menu-panel option-panel', leaving && 'is-leaving')}
          style={{ '--menu-start': `${String(start)}px` } as CSSProperties}
          id={panelId}
          inert={leaving}
          role="dialog"
          aria-label={label}
          onKeyDown={moveWithArrows}
        >
          {heading !== undefined && (
            <div className="menu-top">
              <MenuSearch
                inputRef={searchRef}
                value={query}
                onChange={setQuery}
                onEnter={first && choose(first)}
              />
              {onPin && <p className="menu-note">{t.menus.pinHint}</p>}
            </div>
          )}
          <div className="menu-body">
            {heading === undefined ? (
              <ul className="menu-list">{items.map(row)}</ul>
            ) : (
              <>
                {fallback && found.includes(fallback) && (
                  <ul className="menu-list">{row(fallback)}</ul>
                )}
                {pinned.length > 0 && (
                  <MenuSection label={t.menus.pinned}>
                    <ul className="menu-list">{pinned.map(row)}</ul>
                  </MenuSection>
                )}
                {unpinned.length > 0 && (
                  <MenuSection label={heading}>
                    <ul className="menu-list">{unpinned.map(row)}</ul>
                  </MenuSection>
                )}
                {!first && (
                  <p className="menu-empty" role="status">
                    {t.menus.noMatch(query.trim())}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
